<template>
  <q-card bordered flat class="bg-white">
    <q-card-section class="bg-primary text-white row items-center no-wrap">
      <q-icon name="storage" size="md" class="q-mr-sm" />
      <div class="text-h6">Database Backup & Restore</div>
    </q-card-section>

    <q-card-section class="q-pa-md">
      <div class="text-subtitle1 text-weight-bold q-mb-sm">Export Data</div>
      <p class="text-body2 text-grey-8">
        Choose what to include in your download.
      </p>

      <q-option-group
        v-model="exportScope"
        :options="[
      { label: 'Everything (Teams, Games, Transactions)', value: 'all' },
      { label: 'Teams & Settings Only', value: 'teams_settings' },
    ]"
        color="primary"
        class="q-mb-md"
      />

      <q-btn
        color="primary"
        icon="cloud_download"
        :label="exportScope === 'all' ? 'Export Full Database' : 'Export Teams & Settings'"
        unelevated
        @click="handleExport"
        :loading="isExporting"
      />
    </q-card-section>

    <q-separator />

    <q-card-section class="q-pa-md">
      <div class="text-subtitle1 text-weight-bold q-mb-sm">Export Current Game (CSV)</div>
      <p class="text-body2 text-grey-8">
        Download a spreadsheet-friendly log of the currently loaded game — every pitch, play, and
        runner advance, one row each.
      </p>
      <q-btn
        color="secondary"
        icon="table_view"
        label="Export Game to CSV"
        unelevated
        :disable="!gameStore.activeGameSummary"
        @click="handleCsvExport"
        :loading="isExportingCsv"
      />
    </q-card-section>

    <q-separator />

    <q-card-section class="q-pa-md">
      <div class="text-subtitle1 text-weight-bold q-mb-sm text-negative">Restore Data</div>
      <p class="text-body2 text-grey-8">
        <strong>Warning:</strong> Restoring from JSON will completely overwrite your current offline database. Select the backup file you wish to restore.
      </p>

      <q-file
        v-model="importFile"
        label="Select Backup File..."
        outlined
        bottom-slots
        accept=".json"
      >
        <template v-slot:prepend>
          <q-icon name="cloud_upload" />
        </template>
        <template v-slot:hint>
          Select your consolidated backup file
        </template>

        <!-- Clear selection button -->
        <template v-slot:append v-if="importFile">
          <q-icon name="close" @click.stop.prevent="importFile = null" class="cursor-pointer" />
        </template>
      </q-file>

      <q-btn
        class="q-mt-md"
        color="negative"
        icon="warning"
        label="Overwrite & Restore"
        unelevated
        :disable="!importFile"
        @click="handleImport"
        :loading="isImporting"
      />
    </q-card-section>
  </q-card>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useGameStore } from 'stores/game-store'
import { useQuasar } from 'quasar'

const gameStore = useGameStore()
const $q = useQuasar()

const isExporting = ref(false)
const isImporting = ref(false)
const importFile = ref<File | null>(null)

async function handleImport() {
  if (!importFile.value) return

  const text = await importFile.value.text()
  let parsed: Record<string, unknown>
  try {
    parsed = JSON.parse(text)
  } catch {
    $q.notify({ type: 'negative', message: 'Invalid JSON file.', position: 'top' })
    return
  }

  const presentTables = Object.keys(parsed)
  const touchesGameData = presentTables.some((t) =>
    ['games', 'transactions', 'plateAppearances'].includes(t),
  )

  const message = touchesGameData
    ? 'Are you sure? This will completely wipe your current offline game data and replace it with the uploaded file.'
    : 'This will restore Teams and/or Settings from the uploaded file. It will not affect your current game data.'

  $q.dialog({
    title: 'Confirm Restore',
    message,
    cancel: true,
    persistent: true,
    color: touchesGameData ? 'negative' : 'primary',
  }).onOk(() => {
    const processImport = async () => {
      isImporting.value = true
      try {
        await gameStore.importDatabaseFromJSON(text, presentTables)
        $q.notify({
          type: 'positive',
          message: 'Restore successful! Reloading app...',
          position: 'top',
        })
        importFile.value = null
        setTimeout(() => window.location.reload(), 1500)
      } catch (error) {
        console.error('Import failed', error)
        $q.notify({ type: 'negative', message: 'Error processing JSON file.', position: 'top' })
      } finally {
        isImporting.value = false
      }
    }
    processImport().catch((err) => console.error('Unhandled import error:', err))
  })
}

const exportScope = ref<'all' | 'teams_settings'>('all')

async function handleExport() {
  isExporting.value = true
  try {
    if (exportScope.value === 'teams_settings') {
      await gameStore.exportDatabaseToJSON(['teams', 'users'], 'pitchtracker_teams_settings')
    } else {
      await gameStore.exportDatabaseToJSON()
    }
    $q.notify({
      type: 'positive',
      message: 'Export complete! Check your downloads.',
      position: 'top',
    })
  } catch (error) {
    console.error('Export failed', error)
    $q.notify({ type: 'negative', message: 'Failed to export database.', position: 'top' })
  } finally {
    isExporting.value = false
  }
}

const isExportingCsv = ref(false)
function handleCsvExport() {
  isExportingCsv.value = true
  try {
    gameStore.exportGameToCSV()
    $q.notify({ type: 'positive', message: 'CSV exported!', position: 'top' })
  } catch (error) {
    console.error('CSV export failed', error)
    $q.notify({ type: 'negative', message: 'Failed to export CSV.', position: 'top' })
  } finally {
    isExportingCsv.value = false
  }
}
</script>
